import React, { useState, useRef } from 'react';
import { XIcon, WalletIcon, CreditCardIcon, BuildingIcon, UploadIcon, ImageIcon, CheckCircleIcon } from 'lucide-react';
interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
  onConfirm: (amount: number) => void;
}
export function TopUpModal({
  isOpen,
  onClose,
  currentBalance,
  onConfirm
}: TopUpModalProps) {
  const [amount, setAmount] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [transactionNumber, setTransactionNumber] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  if (!isOpen) return null;
  const quickAmounts = [5000, 10000, 25000, 50000];
  const numAmount = parseInt(amount) || 0;
  // For NEFT, also require transaction number and screenshot
  const isNeftValid = paymentMethod === 'neft' ? transactionNumber.trim().length >= 6 && screenshot !== null : true;
  const isValid = numAmount >= 1000 && numAmount <= 100000 && isNeftValid;
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleConfirm = () => {
    onConfirm(numAmount);
    // Reset NEFT fields
    setTransactionNumber('');
    setScreenshot(null);
    setScreenshotPreview(null);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-352">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col" data-id="element-353">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-354">
          <h2 className="text-lg font-bold text-gray-900 flex items-center" data-id="element-355">
            <WalletIcon size={20} className="mr-2 text-purple-600" data-id="element-356" />
            Add Funds to Wallet
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-357">
            <XIcon size={20} data-id="element-358" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1" data-id="element-359">
          <div className="mb-6 bg-purple-50 p-4 rounded-lg border border-purple-100 flex justify-between items-center" data-id="element-360">
            <span className="text-sm text-purple-800 font-medium" data-id="element-361">
              Current Balance
            </span>
            <span className="text-xl font-bold text-purple-900" data-id="element-362">
              ₹{currentBalance.toLocaleString()}
            </span>
          </div>
          <div className="mb-6" data-id="element-363">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-364">
              Amount to Add (₹)
            </label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Min ₹1,000" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg font-medium" data-id="element-365" />
            <div className="grid grid-cols-4 gap-2 mt-3" data-id="element-366">
              {quickAmounts.map(amt => <button key={amt} onClick={() => setAmount(amt.toString())} className="px-2 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium transition-colors" data-id="element-367">
                  +₹{amt / 1000}k
                </button>)}
            </div>
          </div>
          <div className="mb-6" data-id="element-368">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-369">
              Payment Method
            </label>
            <div className="space-y-2" data-id="element-370">
              <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${paymentMethod === 'card' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-371">
                <input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} className="mr-3 text-purple-600 focus:ring-purple-500" data-id="element-372" />
                <div className="flex items-center" data-id="element-373">
                  <CreditCardIcon size={20} className="text-gray-600 mr-2" data-id="element-374" />
                  <span className="text-sm font-medium text-gray-900" data-id="element-375">
                    Visa ending in 4242
                  </span>
                </div>
              </label>
              <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${paymentMethod === 'upi' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-376">
                <input type="radio" name="payment" value="upi" checked={paymentMethod === 'upi'} onChange={() => setPaymentMethod('upi')} className="mr-3 text-purple-600 focus:ring-purple-500" data-id="element-377" />
                <div className="flex items-center" data-id="element-378">
                  <span className="w-5 h-5 bg-gray-200 rounded flex items-center justify-center text-[10px] font-bold text-gray-600 mr-2" data-id="element-379">
                    UPI
                  </span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-380">
                    9876543210@upi
                  </span>
                </div>
              </label>
              <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${paymentMethod === 'neft' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-381">
                <input type="radio" name="payment" value="neft" checked={paymentMethod === 'neft'} onChange={() => setPaymentMethod('neft')} className="mr-3 text-purple-600 focus:ring-purple-500" data-id="element-382" />
                <div className="flex items-center" data-id="element-383">
                  <BuildingIcon size={20} className="text-gray-600 mr-2" data-id="element-384" />
                  <span className="text-sm font-medium text-gray-900" data-id="element-385">
                    NEFT / Bank Transfer
                  </span>
                </div>
              </label>
            </div>
          </div>
          {/* NEFT Details Section */}
          {paymentMethod === 'neft' && <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4" data-id="element-386">
              <div className="text-sm text-amber-800" data-id="element-387">
                <p className="font-semibold mb-2" data-id="element-388">Bank Account Details:</p>
                <div className="bg-white rounded-lg p-3 space-y-1 text-xs border border-amber-100" data-id="element-389">
                  <p data-id="element-390">
                    <span className="text-gray-500" data-id="element-391">Account Name:</span>{' '}
                    <span className="font-medium" data-id="element-392">
                      Platform Services Pvt Ltd
                    </span>
                  </p>
                  <p data-id="element-393">
                    <span className="text-gray-500" data-id="element-394">Account No:</span>{' '}
                    <span className="font-medium" data-id="element-395">1234567890123456</span>
                  </p>
                  <p data-id="element-396">
                    <span className="text-gray-500" data-id="element-397">IFSC Code:</span>{' '}
                    <span className="font-medium" data-id="element-398">HDFC0001234</span>
                  </p>
                  <p data-id="element-399">
                    <span className="text-gray-500" data-id="element-400">Bank:</span>{' '}
                    <span className="font-medium" data-id="element-401">HDFC Bank, Mumbai</span>
                  </p>
                </div>
              </div>
              <div data-id="element-402">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-403">
                  Transaction / Reference Number{' '}
                  <span className="text-red-500" data-id="element-404">*</span>
                </label>
                <input type="text" value={transactionNumber} onChange={e => setTransactionNumber(e.target.value)} placeholder="Enter NEFT transaction number" className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm" data-id="element-405" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-406">
                  Minimum 6 characters
                </p>
              </div>
              <div data-id="element-407">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-408">
                  Upload Payment Screenshot{' '}
                  <span className="text-red-500" data-id="element-409">*</span>
                </label>
                {!screenshotPreview ? <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-all" data-id="element-410">
                    <UploadIcon size={24} className="mx-auto text-gray-400 mb-2" data-id="element-411" />
                    <p className="text-sm text-gray-600" data-id="element-412">
                      Click to upload screenshot
                    </p>
                    <p className="text-xs text-gray-400 mt-1" data-id="element-413">
                      PNG, JPG up to 5MB
                    </p>
                  </div> : <div className="relative border border-gray-200 rounded-lg overflow-hidden" data-id="element-414">
                    <img src={screenshotPreview} alt="Payment screenshot" className="w-full h-32 object-cover" data-id="element-415" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" data-id="element-416">
                      <button onClick={handleRemoveScreenshot} className="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors" data-id="element-417">
                        Remove
                      </button>
                    </div>
                    <div className="absolute top-2 right-2 bg-green-500 text-white p-1 rounded-full" data-id="element-418">
                      <CheckCircleIcon size={14} data-id="element-419" />
                    </div>
                  </div>}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" data-id="element-420" />
              </div>
            </div>}
          <button onClick={handleConfirm} disabled={!isValid} className="w-full py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-421">
            {paymentMethod === 'neft' ? 'Submit for Verification' : `Pay ₹${numAmount.toLocaleString()}`}
          </button>
          <p className="text-xs text-center text-gray-500 mt-3" data-id="element-422">
            {paymentMethod === 'neft' ? 'Your payment will be verified within 24 hours' : 'Secure payment processing via Razorpay'}
          </p>
        </div>
      </div>
    </div>;
}